import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  User,
  BookOpen,
  CheckCircle,
  XCircle,
  Hash,
  FileText,
  Download,
  GraduationCap
} from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { sanitizeInput } from '../../utils/sanitize';
import { navigateBack } from '../../utils/navigation';


const StudentView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrollments, setEnrollments] = useState([]);

    useEffect(() => {
        fetchStudentDetails();
    }, [id]);

    const fetchStudentDetails = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/users/${id}`);
            if (data.success) {
                setStudent(data.data);
                
                // Fetch enrollments if available
                try {
                    const enrollRes = await api.get(`/enrollments?student=${id}`);
                    if (enrollRes.data.success) {
                        const sanitizedEnrollments = enrollRes.data.data.map(enrollment => ({
                            ...enrollment,
                            course: enrollment.course ? {
                                ...enrollment.course,
                                title: sanitizeInput(enrollment.course.title)
                            } : null,
                            status: sanitizeInput(enrollment.status),
                            enrollmentDate: enrollment.enrollmentDate
                        }));
                        setEnrollments(sanitizedEnrollments);
                    }
                } catch (error) {
                    console.error('Failed to fetch enrollments:', error);
                }
            }
        } catch (err) {
            toast.error('Failed to load student');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
            inactive: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle }
        };

        const badge = badges[status] || badges.inactive;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
                <Icon className="w-4 h-4 mr-1.5" />
                {sanitizeInput(status) ? sanitizeInput(status).charAt(0).toUpperCase() + sanitizeInput(status).slice(1) : 'Active'}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!student) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Student Not Found</h2>
                <p className="text-gray-500 mb-6">The student you're looking for doesn't exist.</p>
                <button
                    onClick={() => navigateBack(navigate, '/college/students')}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigateBack(navigate, '/college/students')}
                        className="p-2.5 bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/45 rounded-2xl shadow-sm hover:bg-slate-50 dark:hover:bg-[#00152e] transition-colors shrink-0"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff]">Student Details</h1>
                        <p className="text-sm text-gray-500 mt-1">Complete information about the student</p>
                    </div>
                </div>
            </div>

            {/* Student Profile Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="h-32 bg-gradient-to-r from-primary-500 to-secondary-600"></div>
                <div className="px-8 pb-8">
                    <div className="flex items-start -mt-16 mb-6">
                        <div className="w-32 h-32 bg-white rounded-full border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                            {student.profileImage ? (
                                <img
                                    src={getFileUrl(student.profileImage)}
                                    className="w-full h-full object-cover rounded-full"
                                    alt="Profile"
                                />
                            ) : (
                                <div className="w-28 h-28 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center">
                                    <span className="text-white font-bold text-4xl">
                                        {sanitizeInput(student.fullName)?.charAt(0)?.toUpperCase() || 'S'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="ml-6 mt-20 flex-1">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">{sanitizeInput(student.fullName) || 'N/A'}</h2>
                                    <p className="text-gray-500 mt-1">{sanitizeInput(student.email) || 'N/A'}</p>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                                        Student
                                    </span>
                                    {getStatusBadge(student.status)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Information Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Contact Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                            
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(student.email) || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Phone className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Mobile</p>
                                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(student.mobile) || 'N/A'}</p>
                                </div>
                            </div>

                        </div>

                        {/* Academic Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic Information</h3>
                            
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Hash className="w-5 h-5 text-[#1a3884]" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Roll Number</p>
                                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(student.rollNumber) || 'N/A'}</p>
                                </div>
                            </div>


                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-teal-100 rounded-lg">
                                    <GraduationCap className="w-5 h-5 text-teal-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Degree / Program</p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {student.degree ? (
                                            <span>
                                                {sanitizeInput(student.degree.fullName)} ({Array.isArray(student.degree.specialization) ? student.degree.specialization.join(', ') : sanitizeInput(student.degree.specialization)})
                                            </span>
                                        ) : 'No Degree Assigned'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                    <Calendar className="w-5 h-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Batches</p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {sanitizeInput(student.batch) || 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>

            {/* Enrollments (for students) */}
            {enrollments.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <BookOpen className="w-5 h-5 mr-2 text-primary-600" />
                        Course Enrollments
                    </h3>
                    <div className="space-y-3">
                        {enrollments.map((enrollment) => (
                            <div
                                key={enrollment._id}
                                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-4"
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-lg flex items-center justify-center">
                                        <BookOpen className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">
                                            {enrollment.course?.title || 'N/A'}
                                        </h4>
                                        <p className="text-sm text-gray-500">
                                            Enrolled: {enrollment.enrollmentDate ? (() => {
                                                const date = new Date(enrollment.enrollmentDate);
                                                return !isNaN(date) ? date.toLocaleDateString() : 'N/A';
                                            })() : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end sm:space-x-4 w-full sm:w-auto">
                                    <div className="text-right flex-1 sm:flex-none">
                                        <p className="text-sm font-semibold text-gray-900">
                                            {enrollment.overallProgress}% Complete
                                        </p>
                                        <div className="w-full sm:w-32 h-2 bg-gray-200 rounded-full mt-1">
                                            <div
                                                className="h-full bg-gradient-to-r from-primary-500 to-secondary-600 rounded-full"
                                                style={{ width: `${enrollment.overallProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        {getStatusBadge(enrollment.status)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentView;
